import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import teacherService from '../../services/api/teacher';
import adminService from '../../services/api/admin';
import { 
  Save, 
  Upload, 
  Plus, 
  Trash2, 
  ArrowRight, 
  Image as ImageIcon, 
  Info,
  Layout,
  FileText,
  Eye,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Calendar,
  Clock,
  Layers,
  Settings,
  Sparkles,
  Gift,
  CreditCard,
  Lock,
  Unlock,
  XCircle,
  X
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';
import DirectVideoUpload from '../../components/teacher/DirectVideoUpload';
import { formatDuration } from '../../utils/videoUtils';

// Dynamic description function for settings
const getSettingDescription = (setting, value) => {
  const descriptions = {
    isFree: {
      true: 'متاح للجميع بدون اشتراك',
      false: 'يتطلب اشتراك فعال'
    },
    isLockedForNonSubscribers: {
      true: 'مقفول على غير المشتركين فقط',
      false: 'متاح للجميع'
    },
    isActive: {
      true: 'متاح للطلاب حالياً',
      false: 'غير متاح مؤقتاً'
    },
    isPublished: {
      true: 'منشور للطلاب',
      false: 'مسودة - غير منشور'
    }
  };
  
  return descriptions[setting]?.[value] || '';
};

// Enhanced setting titles with icons
const getSettingTitle = (setting, value) => {
  const titles = {
    isFree: {
      true: 'درس مجاني',
      false: 'درس مدفوع'
    },
    isLockedForNonSubscribers: {
      true: 'مقفول للمشتركين',
      false: 'متاح للجميع'
    },
    isActive: {
      true: 'درس نشط',
      false: 'درس معطل'
    },
    isPublished: {
      true: 'درس منشور',
      false: 'درس مسودة'
    }
  };
  
  return titles[setting]?.[value] || '';
};

const CreateLesson = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  
  // التحقق من وضع الإدارة
  const isAdminMode = useMemo(() => searchParams.get('mode') === 'admin', [searchParams]);

  // Helper functions for video URLs
  const getVideoEmbedUrl = (videoUrl) => {
    if (!videoUrl) return '';
    
    // YouTube
    const youtubeMatch = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    if (youtubeMatch) return `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1`;
    
    // Vimeo
    const vimeoMatch = videoUrl.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
    
    // Dailymotion
    const dailymotionMatch = videoUrl.match(/dailymotion\.com\/video\/([a-zA-Z0-9]+)/);
    if (dailymotionMatch) return `https://www.dailymotion.com/embed/video/${dailymotionMatch[1]}?autoplay=1`;
    
    return videoUrl;
  };

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    videoUrl: '',
    courseId: '',
    contentType: 'video',
    videoType: 'external', // Default to external
    durationSeconds: null, // Add duration from client
    isFree: false,
    freeUntilDate: '',
    isLockedForNonSubscribers: true,
    thumbnailUrl: '',
    isActive: true,
    order: 0,
    linkedQuizId: '' // حقل ربط الـ Quiz
  });

  // File states
  const [thumbnail, setThumbnail] = useState(null);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // جلب الكورسات الخاصة بالمدرس أو جميع الكورسات للأدمن
  const { data: subjects, isLoading: coursesLoading, error: coursesError } = useQuery(
    ['teacher-courses', isAdminMode],
    () => isAdminMode ? adminService.getCourses() : teacherService.getTeacherCourses(),
    { 
      retry: 1,
      select: (data) => {
        // Extract courses array from response
        const courses = isAdminMode ? (data?.courses || []) : (data?.courses || data?.data?.courses || []);
        return courses;
      }
    }
  );

  // جلب الاختبارات الخاصة بالمدرس
  const { data: quizzes = [], isLoading: quizzesLoading } = useQuery(
    'teacher-quizzes',
    () => teacherService.getTeacherQuizzes(),
    { 
      retry: 1,
      enabled: !isAdminMode, // فقط للمعلمين وليس الأدمن
      select: (data) => {
        return data?.quizzes || [];
      }
    }
  );

  // التحقق من صحة البيانات
  const validateForm = () => {
    const newErrors = {};

    if (!formData.courseId) {
      newErrors.courseId = 'يرجى اختيار الكورس';
    }

    if (!formData.title.trim()) {
      newErrors.title = 'عنوان الدرس مطلوب';
    } else if (formData.title.length < 2 || formData.title.length > 255) {
      newErrors.title = 'يجب أن يكون العنوان بين 2 و 255 حرفاً';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'وصف الدرس طويل جداً (الحد الأقصى 1000 حرف)';
    }
    
    // Validate contentType
    if (!formData.contentType || !['video', 'pdf', 'file'].includes(formData.contentType)) {
      newErrors.contentType = 'نوع المحتوى غير صالح';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // تنفيذ عملية الإنشاء - Routes متخصصة
  const createLessonMutation = useMutation(
    async (lessonData) => {
      // إذا كان admin mode، استخدم adminService
      if (isAdminMode) {
        return adminService.createContent('lessons', lessonData);
      }
      
      // تحديد نوع الدرس والمناسب Route
      if (lessonData instanceof FormData) {
        // تحديد الـ content type من البيانات
        const contentType = lessonData.get('contentType');
        const videoUrl = lessonData.get('videoUrl');
        const hasVideoFile = lessonData.has('video');
        
        if (hasVideoFile) {
          // رفع فيديو مباشر
          return teacherService.createVideoLessonUpload(lessonData);
        } else if (videoUrl && contentType === 'video') {
          // فيديو خارجي
          return teacherService.createVideoLessonExternal(lessonData);
        } else if (contentType === 'file') {
          // رفع ملف (PDF/Word/Excel)
          return teacherService.createFileLesson(lessonData);
        } else {
          throw new Error('نوع المحتوى غير مدعوم');
        }
      } else {
        // Fallback للبيانات العادية
        if (lessonData.videoUrl) {
          return teacherService.createVideoLessonExternal(lessonData);
        } else {
          throw new Error('بيانات غير صالحة');
        }
      }
    },
    {
      onSuccess: (response) => {
        toast.success('تم إنشاء الدرس بنجاح! يمكنك الآن إضافة المحتوى.');
        queryClient.invalidateQueries(isAdminMode ? ['admin-content', 'lessons'] : 'teacher-lessons');
        navigate(isAdminMode ? '/admin/content' : '/teacher/lessons');
      },
      onError: (error) => {
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
          toast.error('يرجى تصحيح الأخطاء في النموذج');
        } else {
          const errMsg = error.response?.data?.error || error.response?.data?.message || 'فشل في إنشاء الدرس، حاول مرة أخرى.';
          setErrors({ submit: errMsg });
          toast.error(errMsg);
        }
      },
      onSettled: () => {
        setIsSubmitting(false);
      }
    }
  );

  // Handle direct upload completion
  const handleDirectUploadComplete = (lesson) => {
    try {
      toast.success('تم رفع الفيديو وإنشاء الدرس بنجاح!');
      queryClient.invalidateQueries('teacher-lessons');
      navigate('/teacher/lessons');
    } catch (error) {
      toast.error('تم رفع الفيديو بنجاح، لكن حدث خطأ في الانتقال');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error('حجم الصورة كبير جداً (الأقصى 50 ميجابايت)');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error('يجب اختيار ملف صورة صالح');
        return;
      }

      setThumbnail(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeThumbnail = () => {
    setThumbnail(null);
    setThumbnailPreview('');
  };

  const handleFileChange = (e) => {
    const uploadedFile = e.target.files[0];
    if (uploadedFile) {
      // Validate file size (100MB max for PDFs/documents)
      if (uploadedFile.size > 100 * 1024 * 1024) {
        toast.error('حجم الملف كبير جداً (الأقصى 100 ميجابايت)');
        return;
      }
      
      // Allowed file types
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/plain',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      
      if (!allowedTypes.includes(uploadedFile.type)) {
        toast.error('نوع الملف غير مدعوم. يرجى استخدام PDF, Word, PowerPoint, Excel, أو Text');
        return;
      }

      setFile(uploadedFile);
      
      // Create file preview info
      const fileSizeInMB = (uploadedFile.size / (1024 * 1024)).toFixed(2);
      const fileExtension = uploadedFile.name.split('.').pop().toUpperCase();
      
      setFilePreview({
        name: uploadedFile.name,
        size: fileSizeInMB,
        type: fileExtension
      });
    }
  };

  const removeFile = () => {
    setFile(null);
    setFilePreview(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('يرجى التحقق من كافة الحقول المطلوبة');
      return;
    }

    // If using direct upload, don't submit form normally (DirectVideoUpload handles it)
    if (formData.videoType === 'upload' && formData.contentType === 'video') {
      toast.info('يرجى استخدام زر الرفع المباشر لرفع الفيديو');
      return;
    }

    setIsSubmitting(true);
    
    // Prepare data for submission
    let lessonData;
    
    // Always use FormData for file upload support (النمط الاحترافي الموحد - القاعدة الذهبية)
    lessonData = new FormData();
    lessonData.append('courseId', String(formData.courseId));
    lessonData.append('title', formData.title.trim());
    lessonData.append('description', formData.description || '');
    lessonData.append('contentType', formData.contentType);
    
    // booleans - explicit string conversion
    lessonData.append('isFree', String(formData.isFree));
    lessonData.append('isLockedForNonSubscribers', String(formData.isLockedForNonSubscribers));
    
    // numbers - append only if not null/undefined/empty
    if (formData.order != null && formData.order !== '')
      lessonData.append('order', String(formData.order));
    
    if (formData.durationSeconds != null && formData.durationSeconds !== '')
      lessonData.append('durationSeconds', String(formData.durationSeconds));
    
    if (formData.contentType === 'video') {
      lessonData.append('videoType', formData.videoType);
      if (formData.videoType === 'external' && formData.videoUrl) {
        // فيديو خارجي
        lessonData.append('videoUrl', formData.videoUrl);
      }
      // Note: Direct upload is handled via DirectVideoUpload component
    } else if (formData.contentType === 'file' && file) {
      // رفع ملف (PDF/Word/Excel/etc)
      lessonData.append('file', file);
    }
    // For text content, no additional files needed
    
    // Add thumbnail if exists
    if (thumbnail) {
      lessonData.append('thumbnail', thumbnail);
    }
    
    // Only add freeUntilDate if it has a value
    if (formData.freeUntilDate) {
      lessonData.append('freeUntilDate', formData.freeUntilDate);
    }
    
    // Add linkedQuizId if selected
    if (formData.linkedQuizId) {
      lessonData.append('linkedQuizId', formData.linkedQuizId);
    }
    
    // Lesson data prepared for submission

    createLessonMutation.mutate(lessonData);
  };

  // معالجة حالات التحميل والخطأ
  if (coursesLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50/50">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse">جاري تحميل الكورسات...</p>
      </div>
    );
  }

  if (coursesError) {
    return (
      <div className="max-w-xl mx-auto py-20 px-6 text-center">
        <div className="bg-white rounded-[2.5rem] shadow-xl p-10 border border-red-100">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h2 className="text-2xl font-black text-gray-900 mb-2">خطأ في تحميل الكورسات</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            {coursesError?.message || 'لم يتم العثور على كورسات متاحة.'}
          </p>
          <button
            onClick={() => navigate(isAdminMode ? '/admin/content' : '/teacher/lessons')}
            className="bg-primary-600 text-white px-10 py-4 rounded-2xl font-bold hover:bg-primary-700 transition-all flex items-center justify-center gap-2 mx-auto shadow-lg"
          >
            <ArrowRight size={18} /> العودة {isAdminMode ? 'لإدارة المحتوى' : 'للدروس'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-10 px-4 sm:px-6 lg:px-8 space-y-10 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 animate-in fade-in slide-in-from-top duration-500">
        <div className="space-y-1">
          <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            <div className="bg-green-600 text-white p-1.5 rounded-xl"><Sparkles size={24} /></div>
            إنشاء درس جديد
          </h1>
          <p className="text-gray-500 mt-2 font-medium flex items-center gap-2">
                <Plus className="text-primary-600" size={20} />
                أضف درساً جديداً لكورساتك بكل سهولة ومرونة
          </p>
        </div>
        <button
          onClick={() => navigate(isAdminMode ? '/admin/content' : '/teacher/lessons')}
          className="group flex items-center text-gray-400 hover:text-primary-600 font-bold transition-all mb-2 text-sm"
        >
          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          العودة {isAdminMode ? 'لإدارة المحتوى' : 'للدروس'}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Main Content Area */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10">
            <h2 className="text-xl font-black text-gray-900 mb-8 flex items-center gap-3">
                <Layout size={20} className="text-primary-600" />
                معلومات الدرس الأساسية
            </h2>

            <div className="space-y-6">
              {/* Title Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <FileText size={16} className="text-primary-500" /> عنوان الدرس *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="أدخل عنوان الدرس"
                  className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 placeholder:text-gray-300"
                  required
                />
                {errors.title && (
                  <p className="flex items-center justify-center gap-1.5 text-xs font-bold text-red-500 text-center animate-pulse"><AlertCircle size={12}/> {errors.title}</p>
                )}
              </div>

              {/* Course Selection */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <Layers size={16} className="text-primary-500" /> الكورس الدراسي *
                </label>
                <div className="relative">
                    <select
                    id="courseId"
                    name="courseId"
                    value={formData.courseId}
                    onChange={handleInputChange}
                    className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                    required
                    >
                    <option value="">اختر الكورس المناسب</option>
                    {subjects?.map((course) => (
                        <option key={course.id} value={course.id}>
                        {course.title}
                        </option>
                    ))}
                    </select>
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                        <ChevronRight className="rotate-90" size={20} />
                    </div>
                </div>
                {errors.courseId && (
                  <p className="flex items-center justify-center gap-1.5 text-xs font-bold text-red-500 text-center animate-pulse"><AlertCircle size={12}/> {errors.courseId}</p>
                )}
              </div>

              {/* Description Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <FileText size={16} className="text-primary-500" /> وصف الدرس وتفاصيله
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={5}
                  placeholder="اكتب وصفاً مفصلاً للدرس..."
                  className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-medium text-gray-700 resize-none leading-relaxed"
                />
                <p className="text-[10px] text-gray-400 font-bold px-2 text-left">{formData.description.length} حرف تم استخدامه</p>
                {errors.description && (
                  <p className="flex items-center justify-center gap-1.5 text-xs font-bold text-red-500 text-center animate-pulse"><AlertCircle size={12}/> {errors.description}</p>
                )}
              </div>

              {/* Content Type Selection */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <FileText size={16} className="text-primary-500" /> نوع المحتوى
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, contentType: 'video' }))} 
                    className={`p-4 rounded-2xl border-2 transition-all font-bold text-sm ${
                      formData.contentType === 'video'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Eye size={20} className="mx-auto mb-2" />
                    فيديو
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, contentType: 'file', videoUrl: '', videoType: 'external' }))} 
                    className={`p-4 rounded-2xl border-2 transition-all font-bold text-sm ${
                      formData.contentType === 'file'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Upload size={20} className="mx-auto mb-2" />
                    ملف
                  </button>
                </div>
              </div>

              {/* Video Type Selection - Only show for video content */}
              {formData.contentType === 'video' && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <Eye size={16} className="text-primary-500" /> مصدر الفيديو
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, videoType: 'external', videoFile: null, videoPreview: null }))} 
                    className={`p-4 rounded-2xl border-2 transition-all font-bold text-sm ${
                      formData.videoType === 'external'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Upload className="w-5 h-5 mx-auto mb-2" />
                    رابط خارجي
                    <p className="text-xs opacity-75">YouTube, Vimeo</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, videoType: 'upload', videoUrl: '' }))} 
                    className={`p-4 rounded-2xl border-2 transition-all font-bold text-sm ${
                      formData.videoType === 'upload'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <FileText className="w-5 h-5 mx-auto mb-2" />
                    رفع مباشر
                    <p className="text-xs opacity-75">MP4, AVI, MOV</p>
                  </button>
                </div>
              </div>
              )}

              {/* Video Input Based on Type - Only show for video content */}
              {formData.contentType === 'video' && formData.videoType === 'external' && (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                      <Eye size={16} className="text-primary-500" /> رابط الفيديو الخارجي
                  </label>
                  <input
                    type="text"
                    id="videoUrl"
                    name="videoUrl"
                    value={formData.videoUrl}
                    onChange={handleInputChange}
                    placeholder="https://youtube.com/watch?v=... أو https://vimeo.com/..."
                    className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 placeholder:text-gray-300"
                  />
                  {formData.videoUrl && (
                    <div className="mt-4">
                      <p className="text-xs text-gray-500 font-medium mb-2">معاينة الفيديو:</p>
                      <div className="aspect-video bg-black rounded-2xl overflow-hidden shadow-lg">
                        <iframe
                          src={getVideoEmbedUrl(formData.videoUrl)}
                          className="w-full h-full"
                          title="Video Preview"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  )}

                  {/* Duration Input - Required for all external videos */}
                  <div className="mt-4 space-y-2">
                    <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                        <Clock size={16} className="text-primary-500" /> مدة الفيديو (بالثواني)
                        <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="durationSeconds"
                      name="durationSeconds"
                      value={formData.durationSeconds || ''}
                      onChange={handleInputChange}
                      placeholder="مثال: 300 (لـ 5 دقائق)"
                      min="1"
                      max="14400" // 4 ساعات كحد أقصى
                      className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 placeholder:text-gray-300"
                      required
                    />
                    <p className="text-xs text-gray-500 font-medium">
                        💡 أدخل مدة الفيديو بالثواني (مثال: 600 = 10 دقائق)
                    </p>
                    {formData.durationSeconds && (
                      <p className="text-xs text-green-600 font-medium">
                        ⏱️ المدة: {formatDuration(formData.durationSeconds)}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* File Upload - Only show for file content */}
              {formData.contentType === 'file' && (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                      <Upload size={16} className="text-primary-500" /> رفع الملف (PDF, Word, Excel, إلخ)
                  </label>
                  
                  <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center hover:border-primary-400 transition-colors">
                    {filePreview ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-center gap-4 bg-gray-50 rounded-xl p-6">
                          <div className="bg-primary-100 text-primary-600 p-4 rounded-lg">
                            <FileText size={32} />
                          </div>
                          <div className="text-left">
                            <p className="font-bold text-gray-900">{filePreview.name}</p>
                            <p className="text-sm text-gray-500">{filePreview.size} MB • {filePreview.type}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removeFile}
                          className="bg-red-600 text-white px-6 py-3 rounded-2xl hover:bg-red-700 transition-all font-bold flex items-center gap-2 mx-auto"
                        >
                          <Trash2 size={20} />
                          حذف الملف
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <Upload className="w-16 h-16 text-gray-400 mx-auto" />
                        <div>
                          <p className="text-lg font-bold text-gray-700">اختر ملفاً لرفعه</p>
                          <p className="text-sm text-gray-500">PDF, Word, Excel, PowerPoint, Text (حتى 100MB)</p>
                        </div>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                          onChange={handleFileChange}
                          className="hidden"
                          id="file-upload"
                        />
                        <label
                          htmlFor="file-upload"
                          className="bg-primary-600 text-white px-8 py-4 rounded-2xl hover:bg-primary-700 transition-all font-bold cursor-pointer inline-flex items-center gap-2"
                        >
                          <Plus size={20} />
                          اختر ملف
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Video Upload - Only show for direct upload type */}
              {formData.contentType === 'video' && formData.videoType === 'upload' && (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                      <Upload size={16} className="text-primary-500" /> رفع الفيديو
                  </label>
                  
                  <DirectVideoUpload
                    courseId={formData.courseId}
                    initialData={{
                      title: formData.title,
                      description: formData.description,
                      durationSeconds: formData.durationSeconds,
                      order: formData.order,
                      isFree: formData.isFree,
                      freeUntilDate: formData.freeUntilDate,
                      isLockedForNonSubscribers: formData.isLockedForNonSubscribers
                    }}
                    thumbnailFile={thumbnail} // Pass thumbnail from parent
                    onUploadComplete={handleDirectUploadComplete}
                    disabled={!formData.courseId || !formData.title}
                  />
                </div>
              )}

              {/* Free Until Date Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <Calendar size={16} className="text-primary-500" /> متاح مجاناً حتى تاريخ (اختياري)
                </label>
                <input
                  type="date"
                  id="freeUntilDate"
                  name="freeUntilDate"
                  value={formData.freeUntilDate}
                  onChange={handleInputChange}
                  className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 placeholder:text-gray-300"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Settings Area */}
        <div className="space-y-6">
          
          {/* Thumbnail Section */}
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8">
            <h2 className="text-lg font-black text-gray-900 mb-6 flex items-center gap-3">
                <ImageIcon size={18} className="text-primary-600" /> صورة الدرس
            </h2>

            <div className="space-y-6">
              {thumbnailPreview ? (
                <div className="relative group rounded-3xl overflow-hidden shadow-xl ring-4 ring-gray-50 transition-all">
                  <img
                    src={thumbnailPreview}
                    alt="Preview"
                    className="w-full aspect-video object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button
                      type="button"
                      onClick={removeThumbnail}
                      className="bg-red-600 text-white p-3 rounded-2xl hover:bg-red-700 transition-all shadow-lg transform hover:scale-110"
                      title="حذف الصورة"
                    >
                      <X size={24} />
                    </button>
                  </div>
                </div>
              ) : (
                <label 
                  htmlFor="thumbnail"
                  className="w-full aspect-video border-4 border-dashed border-gray-100 rounded-[2rem] flex flex-col items-center justify-center group hover:border-primary-200 hover:bg-primary-50/30 transition-all cursor-pointer"
                >
                  <div className="bg-primary-50 text-primary-600 p-4 rounded-2xl mb-3 group-hover:scale-110 transition-transform">
                    <Upload size={32} />
                  </div>
                  <span className="text-sm font-black text-gray-900">اضغط لرفع صورة</span>
                  <span className="text-[10px] font-bold text-gray-400 mt-1">JPG, PNG (Max 50MB)</span>
                </label>
              )}
              
              <input
                type="file"
                id="thumbnail"
                accept="image/*"
                onChange={handleThumbnailChange}
                className="hidden"
              />
              
              {thumbnailPreview && (
                  <label htmlFor="thumbnail" className="w-full py-3 px-4 border border-gray-100 rounded-xl font-bold text-xs text-primary-600 bg-primary-50/50 hover:bg-primary-100 transition-all flex items-center justify-center gap-2 cursor-pointer shadow-sm">
                      <Upload size={14} /> تغيير صورة الدرس
                  </label>
              )}
            </div>
          </div>

          {/* Settings Section */}
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8">
            <h2 className="text-lg font-black text-gray-900 mb-6 flex items-center gap-3 uppercase tracking-tighter">
                <Settings size={18} className="text-primary-600" /> إعدادات الدرس
            </h2>
            
            <div className="space-y-4">
              <div className="bg-gray-50/50 p-5 rounded-2xl border border-gray-100 flex items-center justify-between group">
                <div className="flex flex-col">
                  <span className="text-sm font-black text-gray-700 flex items-center gap-2">
                    {formData.isFree ? <Gift size={14} className="text-green-600" /> : <CreditCard size={14} className="text-blue-600" />}
                    {getSettingTitle('isFree', formData.isFree)}
                  </span>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                    {getSettingDescription('isFree', formData.isFree)}
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    id="isFree"
                    name="isFree"
                    checked={formData.isFree}
                    onChange={handleInputChange}
                    className="sr-only peer"
                  />
                  <div className="w-12 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[-1.25rem] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>

              <div className="bg-gray-50/50 p-5 rounded-2xl border border-gray-100 flex items-center justify-between group">
                <div className="flex flex-col">
                  <span className="text-sm font-black text-gray-700 flex items-center gap-2">
                    {formData.isLockedForNonSubscribers ? <Lock size={14} className="text-orange-600" /> : <Unlock size={14} className="text-purple-600" />}
                    {getSettingTitle('isLockedForNonSubscribers', formData.isLockedForNonSubscribers)}
                  </span>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                    {getSettingDescription('isLockedForNonSubscribers', formData.isLockedForNonSubscribers)}
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    id="isLockedForNonSubscribers"
                    name="isLockedForNonSubscribers"
                    checked={formData.isLockedForNonSubscribers}
                    onChange={handleInputChange}
                    className="sr-only peer"
                  />
                  <div className="w-12 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[-1.25rem] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-orange-600"></div>
                </label>
              </div>

              <div className="bg-gray-50/50 p-5 rounded-2xl border border-gray-100 flex items-center justify-between group">
                <div className="flex flex-col">
                  <span className="text-sm font-black text-gray-700 flex items-center gap-2">
                    {formData.isActive ? <CheckCircle2 size={14} className="text-green-600" /> : <XCircle size={14} className="text-red-600" />}
                    {getSettingTitle('isActive', formData.isActive)}
                  </span>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                    {getSettingDescription('isActive', formData.isActive)}
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    className="sr-only peer"
                  />
                  <div className="w-12 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[-1.25rem] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>
            </div>

            {/* Order Input */}
            <div className="space-y-2 mt-6">
                <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest mr-2">ترتيب العرض (أولوية)</label>
                <div className="relative">
                    <input
                        type="number"
                        id="order"
                        name="order"
                        value={formData.order}
                        onChange={handleInputChange}
                        min="0"
                        className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-900"
                    />
                    <Layers className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                </div>
            </div>

{/* Quiz Binding Section */}
<div className="mt-10 pt-8 border-t border-gray-200">
  <div className="bg-white rounded-2xl p-7 border border-gray-200">

    {/* Header */}
    <div className="flex items-start justify-between mb-6">
      <div>
        <h3 className="text-lg font-bold text-gray-900">
          ربط اختبار بهذا الدرس
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          اختياري
        </p>
      </div>

      <button
        type="button"
        onClick={() =>
          window.open(
            `/teacher/quizzes/create?lesson=${
              formData.courseId ? formData.order : ""
            }`,
            "_blank"
          )
        }
        className="bg-yellow-500 text-white px-4 py-2 rounded-lg 
                   hover:bg-yellow-600 transition-colors 
                   text-sm font-medium"
      >
        إنشاء اختبار
      </button>
    </div>

    {/* Description */}
    <p className="text-sm text-gray-600 mb-6 leading-relaxed">
      يمكنك ربط اختبار موجود بهذا الدرس لتفعيل عملية التقييم للطلاب،
      أو إنشاء اختبار جديد وربطه لاحقاً.
    </p>

    {/* Select */}
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-800">
        اختر الاختبار:
      </label>

      <select
        value={formData.linkedQuizId}
        onChange={(e) =>
          setFormData((prev) => ({
            ...prev,
            linkedQuizId: e.target.value,
          }))
        }
        className="w-full px-4 py-3 rounded-xl 
                   border border-gray-300 
                   focus:border-yellow-500 
                   focus:ring-2 focus:ring-yellow-100 
                   focus:outline-none 
                   bg-white text-gray-700"
        disabled={quizzesLoading}
      >
        <option value="">-- لا يوجد اختبار مرتبط --</option>

        {quizzesLoading ? (
          <option disabled>جاري جلب الاختبارات...</option>
        ) : quizzes.length === 0 ? (
          <option disabled>
            لا توجد اختبارات متاحة - قم بإنشاء واحد أولاً
          </option>
        ) : (
          quizzes.map((quiz) => (
            <option key={quiz.id} value={quiz.id}>
              {quiz.title}
              {quiz.lesson_title && ` (${quiz.lesson_title})`}
            </option>
          ))
        )}
      </select>
    </div>

    {/* Success message */}
    {formData.linkedQuizId && (
      <div className="mt-5 p-3 bg-green-50 border border-green-200 rounded-lg">
        <p className="text-sm text-green-700">
          تم ربط الاختبار بنجاح.
        </p>
      </div>
    )}

  </div>
</div>

            <div className="mt-8 space-y-4">
                {/* Hide submit button when using direct upload */}
                {!(formData.contentType === 'video' && formData.videoType === 'upload') ? (
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-primary-600 text-white py-4 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all flex items-center justify-center gap-3 disabled:opacity-50 transform active:scale-95"
                    >
                        <Save size={20} />
                        {isSubmitting ? 'جاري المعالجة...' : 'إنشاء الدرس'}
                    </button>
                ) : (
                    <div className="w-full bg-gray-300 text-gray-500 py-4 rounded-2xl font-black shadow-xl flex items-center justify-center gap-3">
                        <Upload size={20} />
                        يرجى استخدام زر الرفع المباشر أعلاه
                    </div>
                )}
                <button
                    type="button"
                    onClick={() => navigate(isAdminMode ? '/admin/content' : '/teacher/lessons')}
                    className="w-full bg-white border border-gray-100 text-gray-400 py-4 rounded-2xl font-bold hover:bg-gray-50 transition-all text-sm"
                >
                    إلغاء العملية
                </button>
            </div>
            
            {errors.submit && (
                <div className="mt-4 p-4 bg-red-50 border border-red-100 rounded-2xl">
                    <p className="text-[10px] font-black text-red-600 flex items-center gap-2 uppercase"><AlertCircle size={14}/> {errors.submit}</p>
                </div>
            )}
          </div>

          {/* Quick Tip */}
          <div className="bg-gray-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden group shadow-2xl">
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-primary-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
            <div className="relative z-10">
                <div className="bg-primary-600 w-10 h-10 rounded-xl flex items-center justify-center mb-4">
                    <Info size={20} />
                </div>
                <h3 className="font-black mb-2">نصيحة للمدرس</h3>
                <p className="text-gray-400 text-xs leading-relaxed font-medium">
                    الفيديوهات القصيرة والمركزة تزيد من نسبة المشاهدة بنسبة 60%. حاول جعل مدة الدرس بين 10-20 دقيقة للحصول على أفضل نتائج.
                </p>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
};

export default CreateLesson;
